// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { getIconPath } from '@tcp/core/src/utils/utils';
import classnames from 'classnames';
import BodyCopy from '../../BodyCopy';
import withStyles from '../../../hoc/withStyles';
import StyledTextBox from '../TextBox.style';
import TextBoxLoader from '../../TextBoxLoader/views/TextBoxLoader';
import Image from '../../Image';
import { getLabelFromCopyOrFromTranslation } from '../../../../../utils/labels/labelUtilty';
import GlobalFormValidationLabels, {
  GLOBAL_FORM_VALIDATION_LABELS,
  lblErrEmailRequired,
} from '../../../../../utils/labels/globalFormValidation.labels';

/**
 *
 * @param {Boolean} showSuccessCheck Forcefully show the success mark and success border. Override redux form params.
 * @param {Object} meta redux-form meta object to analyse the success state. Based on this class will be returned.
 * @return {string} Returns a class name if the form success validation criteria matches else blank string.
 */
const getValidationSuccessClass = (enableSuccessCheck, showSuccessCheck, meta) => {
  const { invalid, pristine, asyncValidating, active } = meta;
  return enableSuccessCheck &&
    (showSuccessCheck || (!active && !pristine && !invalid && !asyncValidating))
    ? 'textbox_validation_success'
    : '';
};

const getPlaceHolderStyle = ({ roundTextBox, spc, elemValue }) => {
  let style = 'TextBox__label';
  if (roundTextBox) {
    style = 'TextBox__view';
  } else if (spc && elemValue) {
    style = 'TextBox__label nonActive';
  }
  return style;
};
const shouldShowPlaceholder = ({ roundTextBoxCapsule, meta: { active }, input: { value } }) => {
  if (roundTextBoxCapsule) {
    return !active && !value;
  }
  return true;
};
// eslint-disable-next-line complexity
const TextBox = ({
  className,
  id,
  ariaLabel,
  type,
  placeholder,
  maxLength,
  maxDate,
  input,
  inputRef,
  meta,
  dataLocator,
  disabled,
  showSuccessCheck,
  enableSuccessCheck,
  isRequired,
  errorDataLocator,
  submitError,
  showExplicitError,
  alwaysShowError,
  roundTextBox,
  spc,
  cvvInfo,
  label,
  showIndicatorIcon,
  isAutoSuggestOpen,
  customIconClass,
  value,
  dateFormat,
  isErrorInBold,
  hideError,
  roundTextBoxCapsule,
  hideIndicatorforPLCC = false,
  labels,
  textBoxLoader,
  spcBag,
  fromAddressVerification,
  fromCouponForm,
  customReduxAction,
  onChangeHandler,
  ...others
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const elemValue = input?.value;
  const [inputValue, setInputValue] = useState('');
  const { touched, error = submitError, active } = meta;
  const errorMessagea11yLbl = `textbox__error__${input.name}`;
  const isErrored = (touched && error) || showExplicitError || (alwaysShowError && error);
  const labelStyle = type === 'date' ? { top: '4px' } : {};

  const checkMarkStyle = classnames('success__checkmark', {
    'success__checkmark--date': type === 'date',
  });

  let inputProps = input;
  // Define the debounced function
  const debouncedOnChange = useCallback(
    debounce((event) => {
      input.onChange(event);
    }, 300), // Adjust the delay as needed
    []
  );

  const customOnChange = (e) => {
    const event = e;
    setInputValue(e?.target?.value);
    setTimeout(() => {
      onChangeHandler(event);
    }, 0);

    debouncedOnChange(e);
  };

  useEffect(() => {
    if (customReduxAction && elemValue) {
      setInputValue(elemValue);
    }
  }, [elemValue]);

  useEffect(() => {
    // Cleanup the debounce on unmount
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  const customOnBlur = (e) => {
    const event = e;
    setTimeout(() => {
      input.onBlur(event);
    }, 0);
  };

  const customOnFocus = (e) => {
    const event = e;
    setTimeout(() => {
      input.onFocus(event);
    }, 0);
  };
  if (customReduxAction) {
    inputProps = {
      ...input,
      onBlur: customOnBlur,
      onChange: customOnChange,
      onFocus: customOnFocus,
    };
  }
  let errData;
  if (error === 'undefined' && input.name === 'emailAddress') {
    errData = getLabelFromCopyOrFromTranslation(
      `${GLOBAL_FORM_VALIDATION_LABELS}.${lblErrEmailRequired}`,
      GlobalFormValidationLabels
    );
  } else {
    errData = error;
  }

  return (
    <label
      htmlFor={id}
      className={`${className} input-fields-wrapper ${getValidationSuccessClass(
        enableSuccessCheck,
        showSuccessCheck,
        meta
      )}`}
      aria-label={ariaLabel || placeholder}
    >
      <input
        {...others}
        {...inputProps}
        id={id}
        aria-label={ariaLabel || placeholder}
        className={
          roundTextBox
            ? `${roundTextBoxCapsule ? 'track_order_round_textbox__capsule' : ''} TextBox_view`
            : 'TextBox__input'
        }
        name={input.name}
        disabled={disabled}
        type={type}
        maxLength={maxLength}
        max={maxDate}
        value={customReduxAction ? inputValue : elemValue}
        ref={inputRef}
        placeholder=""
        data-locator={dataLocator}
        aria-required={isRequired}
        aria-describedby={errorMessagea11yLbl}
      />
      <div className={`custom-icon ${customIconClass}`} aria-disabled="true" />
      {showIndicatorIcon && !hideIndicatorforPLCC && isErrored ? (
        <div
          className={`input-right-err-icon ${dataLocator === 'cvvField' ? 'cvv-right-icon' : ''}`}
          aria-disabled="true"
        />
      ) : (
        cvvInfo && (
          <span className="cvv-icon" dataLocator="cvvInfoIcon">
            {cvvInfo}
          </span>
        )
      )}
      {shouldShowPlaceholder({ roundTextBoxCapsule, meta, input }) && (
        <BodyCopy
          className={`${getPlaceHolderStyle({ roundTextBox, spc, elemValue })} ${
            !active && !input.value ? 'TextBox__placeholder' : ''
          } ${roundTextBoxCapsule ? 'TextBox__capsule_extra_padding' : ''}`}
          fontFamily="secondary"
          fontSize="fs12"
          customStyle={labelStyle}
        >
          {active || elemValue ? label || placeholder : placeholder}
        </BodyCopy>
      )}
      {isErrored ? (
        <div className={`TextBox__error ${isAutoSuggestOpen || hideError ? 'hide-err' : ''}`}>
          {!spc && <div className={isErrored ? 'warning-icon' : ''} aria-disabled="true" />}
          <BodyCopy
            color="error"
            component="div"
            fontSize="fs12"
            fontFamily="secondary"
            fontWeight={!spc || isErrorInBold ? 'extrabold' : undefined}
            aria-live="assertive"
            data-locator={errorDataLocator}
            id={errorMessagea11yLbl}
          >
            {errData || showExplicitError}
          </BodyCopy>
        </div>
      ) : null}
      {enableSuccessCheck && (
        <div className={spc ? 'checkmark-container' : ''}>
          {fromCouponForm ? (
            <Image
              className="header-icon elem-pr-XS"
              width="16px"
              height="16px"
              src={getIconPath('circle-check-form-icon')}
              alt="reward-icon"
              data-locator="reward-icon"
              aria-hidden="true"
            />
          ) : (
            <div className={checkMarkStyle} />
          )}
        </div>
      )}

      {textBoxLoader && <TextBoxLoader spc={spc} spcBag={spcBag} />}
    </label>
  );
};

TextBox.defaultProps = {
  id: '',
  ariaLabel: '',
  type: 'text',
  placeholder: '',
  maxDate: '',
  errorDataLocator: '',
  onChangeHandler: () => {},
  dataLocator: '',
  disabled: false,
  meta: {},
  showSuccessCheck: false,
  enableSuccessCheck: true,
  isRequired: false,
  submitError: '',
  showExplicitError: '',
  alwaysShowError: false,
  roundTextBox: false,
  spc: false,
  label: undefined,
  showIndicatorIcon: false,
  isAutoSuggestOpen: false,
  customIconClass: '',
  value: '',
  dateFormat: '',
  isErrorInBold: false,
  hideError: false,
  hideIndicatorforPLCC: false,
  roundTextBoxCapsule: false,
  customReduxAction: false,
};

TextBox.propTypes = {
  className: PropTypes.string.isRequired,
  id: PropTypes.string,
  ariaLabel: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  maxLength: PropTypes.number,
  maxDate: PropTypes.string,
  inputRef: PropTypes.node,
  meta: PropTypes.shape({}),
  dataLocator: PropTypes.string,
  disabled: PropTypes.bool,
  enableSuccessCheck: PropTypes.bool,
  isRequired: PropTypes.bool,
  errorDataLocator: PropTypes.string,
  input: PropTypes.shape({}).isRequired,
  type: PropTypes.string,
  cvvInfo: PropTypes.shape({}),
  showSuccessCheck: PropTypes.bool,
  submitError: PropTypes.string,
  showExplicitError: PropTypes.string,
  alwaysShowError: PropTypes.bool,
  onChangeHandler: PropTypes.func,
  roundTextBox: PropTypes.bool,
  spc: PropTypes.bool,
  showIndicatorIcon: PropTypes.bool,
  isAutoSuggestOpen: PropTypes.bool,
  customIconClass: PropTypes.string,
  value: PropTypes.string,
  dateFormat: PropTypes.string,
  isErrorInBold: PropTypes.bool,
  hideError: PropTypes.bool,
  hideIndicatorforPLCC: PropTypes.bool,
  roundTextBoxCapsule: PropTypes.bool,
  customReduxAction: PropTypes.bool,
};
export default withStyles(TextBox, StyledTextBox);
export { TextBox as TextBoxVanilla };
